import { useCallback, useEffect, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, RotateCw, FileWarning } from 'lucide-react'
import { pdfjsLib } from '@/lib/pdf/pdfWorker'
import { Button } from './Button'
import { Spinner } from './Spinner'
import { cn } from '@/lib/utils'

interface Props {
  /** Letöltési forrás. Pontosan egyet kell adni. */
  bytes?: Uint8Array | null
  url?: string | null
  /** A magasság a konténer width-jétől függetlenül kötött (vertikális scroll). */
  className?: string
}

const MIN_SCALE = 0.5
const MAX_SCALE = 2.5
const SCALE_STEP = 0.2

export function PdfPreview({ bytes, url, className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const renderTaskRef = useRef<{ cancel: () => void } | null>(null)
  const [doc, setDoc] = useState<Awaited<ReturnType<typeof pdfjsLib.getDocument>['promise']> | null>(null)
  const [pageNum, setPageNum] = useState(1)
  const [scale, setScale] = useState(1.0)
  const [rotation, setRotation] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Document betöltés
  useEffect(() => {
    let canceled = false
    setLoading(true)
    setError(null)
    setDoc(null)
    setPageNum(1)

    // FONTOS: a pdfjs Worker.postMessage transferable list-en küldi az ArrayBuffer-t,
    // ami detacholja az eredeti bytes-t. React StrictMode kétszeres mount esetén ez
    // "ArrayBuffer already detached" hibát ad. Mindig új másolatot adunk át.
    const source = bytes
      ? { data: new Uint8Array(bytes) }
      : url
        ? { url }
        : null
    if (!source) {
      setLoading(false)
      setError('Nincs PDF forrás megadva')
      return
    }

    const task = pdfjsLib.getDocument(source)
    task.promise
      .then((d) => {
        if (canceled) return
        setDoc(d)
        setLoading(false)
      })
      .catch((err: unknown) => {
        if (canceled) return
        setError(err instanceof Error ? err.message : 'PDF betöltési hiba')
        setLoading(false)
      })

    return () => {
      canceled = true
      task.destroy().catch(() => {})
    }
  }, [bytes, url])

  // Oldal rendering
  const renderPage = useCallback(async () => {
    if (!doc || !canvasRef.current) return
    if (renderTaskRef.current) {
      renderTaskRef.current.cancel()
      renderTaskRef.current = null
    }
    try {
      const page = await doc.getPage(pageNum)
      const viewport = page.getViewport({ scale, rotation })
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      if (!ctx) return

      const dpr = window.devicePixelRatio || 1
      canvas.width = Math.floor(viewport.width * dpr)
      canvas.height = Math.floor(viewport.height * dpr)
      canvas.style.width = `${viewport.width}px`
      canvas.style.height = `${viewport.height}px`

      const transform = dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : undefined

      // A pdfjs-dist 4.x optional `canvas` mezőt fogad el a renderhez, de a TS típusban
      // még nem szerepel — a `canvasContext` is elegendő, így kihagyjuk.
      const task = page.render({ canvasContext: ctx, viewport, transform })
      renderTaskRef.current = task
      await task.promise
      renderTaskRef.current = null
    } catch (err: unknown) {
      // Render task cancel-elés normális, ne mutassuk hibaként.
      if (err instanceof Error && err.name === 'RenderingCancelledException') return
      setError(err instanceof Error ? err.message : 'Render hiba')
    }
  }, [doc, pageNum, scale, rotation])

  useEffect(() => {
    void renderPage()
    return () => {
      if (renderTaskRef.current) {
        renderTaskRef.current.cancel()
        renderTaskRef.current = null
      }
    }
  }, [renderPage])

  const totalPages = doc?.numPages ?? 0
  const atFirst = pageNum <= 1
  const atLast = pageNum >= totalPages

  function prev() {
    if (!atFirst) setPageNum((p) => p - 1)
  }
  function next() {
    if (!atLast) setPageNum((p) => p + 1)
  }
  function zoomIn() {
    setScale((s) => Math.min(MAX_SCALE, +(s + SCALE_STEP).toFixed(2)))
  }
  function zoomOut() {
    setScale((s) => Math.max(MIN_SCALE, +(s - SCALE_STEP).toFixed(2)))
  }
  function rotate() {
    setRotation((r) => (r + 90) % 360)
  }

  return (
    <div className={cn('flex flex-col bg-white', className)}>
      <Toolbar
        pageNum={pageNum}
        totalPages={totalPages}
        atFirst={atFirst}
        atLast={atLast}
        scale={scale}
        loading={loading}
        onPrev={prev}
        onNext={next}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onRotate={rotate}
      />
      <div className="relative flex-1 overflow-auto scrollbar-thin bg-slate-100">
        {loading && (
          <div className="absolute inset-0 grid place-items-center text-sm text-slate-500">
            <div className="flex flex-col items-center gap-2">
              <Spinner size={24} />
              <span>PDF betöltése…</span>
            </div>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 grid place-items-center px-6 text-center">
            <div>
              <FileWarning className="mx-auto h-10 w-10 text-red-500" />
              <p className="mt-2 text-sm font-medium text-slate-800">Nem sikerült betölteni a PDF-et</p>
              <p className="mt-1 text-xs text-slate-500">{error}</p>
            </div>
          </div>
        )}
        <div className="flex justify-center p-4">
          <canvas ref={canvasRef} className="shadow-panel rounded bg-white" />
        </div>
      </div>
    </div>
  )
}

function Toolbar({
  pageNum,
  totalPages,
  atFirst,
  atLast,
  scale,
  loading,
  onPrev,
  onNext,
  onZoomIn,
  onZoomOut,
  onRotate,
}: {
  pageNum: number
  totalPages: number
  atFirst: boolean
  atLast: boolean
  scale: number
  loading: boolean
  onPrev: () => void
  onNext: () => void
  onZoomIn: () => void
  onZoomOut: () => void
  onRotate: () => void
}) {
  return (
    <div className="flex items-center justify-between gap-2 border-b border-slate-200 bg-white px-3 py-1.5">
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={onPrev}
          disabled={atFirst || loading}
          aria-label="Előző oldal"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="text-xs text-slate-600 tabular-nums min-w-[3.5rem] text-center">
          {loading ? '—' : `${pageNum} / ${totalPages || 1}`}
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={onNext}
          disabled={atLast || loading}
          aria-label="Következő oldal"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex items-center gap-1">
        <Button variant="ghost" size="sm" onClick={onZoomOut} disabled={loading} aria-label="Kicsinyítés">
          <ZoomOut className="h-4 w-4" />
        </Button>
        <span className="text-xs text-slate-600 tabular-nums min-w-[3rem] text-center">
          {Math.round(scale * 100)}%
        </span>
        <Button variant="ghost" size="sm" onClick={onZoomIn} disabled={loading} aria-label="Nagyítás">
          <ZoomIn className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onRotate} disabled={loading} aria-label="Forgatás 90°">
          <RotateCw className="h-4 w-4" />
        </Button>
      </div>
    </div>
  )
}
